import type { ReactNode } from 'react';
import { cn } from './cn';
import { StatusDot } from './Status';
import './hottr-image.css';

export interface MediaCardProps {
  /** Ignored when `media` is supplied. */
  image?: string;
  /**
   * Custom media in place of a plain `<img>` — `.app` passes its procedural
   * EditorialImage here so profiles without a photo still render a scene.
   * The card's crop, scrim, overlays and hover are identical either way.
   */
  media?: ReactNode;
  /** Empty alt is correct when the name below already labels the card. */
  alt?: string;
  name: string;
  /** The consistent second line — "0.4 km · Live" or "South Yarra". */
  meta?: string;
  /** Right-aligned on the name row — `.men` puts the rate here. */
  trailing?: ReactNode;
  /** Top-right marker — "Verified", "Elite". */
  badge?: string;
  /** Top-left pink presence dot. */
  live?: boolean;
  /** Optional plate number, editorial contact-sheet style. */
  index?: number;
  /** One extra muted line — tagline, services. */
  footnote?: string;
  /** Shown if `image` fails to load, so a dead URL never leaves an ink hole. */
  fallback?: string;
  aspect?: 'portrait' | 'square';
  selected?: boolean;
  onClick?: () => void;
  /** Render as a link instead of a button. */
  href?: string;
  /** Router Link component, when the host app routes client-side. */
  as?: React.ElementType;
  eager?: boolean;
  className?: string;
}

const ASPECT = {
  // 9:16 — the canonical roster crop.
  portrait: 'aspect-[9/16]',
  square: 'aspect-square',
} as const;

/**
 * One card skeleton, two products.
 *
 * Shared: portrait crop rules, status position, name treatment, meta line,
 * hover, border and shadow behaviour, typography. Product metadata rides on
 * top via `trailing` / `badge` / `footnote`.
 *
 *   .app  <MediaCard name="KASPER" meta="0.4 km · Live" live />
 *   .men  <MediaCard name="KEVIN PARK" meta="South Yarra" trailing="$500" badge="Verified" />
 *
 * `.men`'s cards were narrower and denser than `.app`'s; this gives both the
 * same image dominance and the same name weight.
 */
export const MediaCard = ({
  image,
  media,
  alt = '',
  name,
  meta,
  trailing,
  badge,
  live,
  index,
  footnote,
  fallback,
  aspect = 'portrait',
  selected,
  onClick,
  href,
  as,
  eager,
  className,
}: MediaCardProps) => {
  const Root = as ?? (href ? 'a' : 'button');
  const rootProps = href ? { [as ? 'to' : 'href']: href } : { type: 'button' as const, onClick };

  return (
    <Root
      {...rootProps}
      onClick={onClick}
      className={cn(
        'group relative block w-full overflow-hidden text-left',
        'rounded-sm border bg-hottr-charcoal',
        'transition-[border-color,box-shadow,transform] duration-base ease-mechanical',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hottr-pink focus-visible:ring-offset-2 focus-visible:ring-offset-hottr-ink',
        ASPECT[aspect],
        selected
          ? 'border-hottr-pink shadow-neon-medium'
          : 'border-white/[0.10] hover:border-hottr-pink/40 hover:shadow-neon-soft',
        className,
      )}
      data-protect-media
    >
      {media ? (
        <div className="absolute inset-0 [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&>*]:h-full [&>*]:w-full">
          {media}
        </div>
      ) : (
      <img
        src={image}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        onError={
          fallback
            ? (e) => {
                const el = e.currentTarget;
                el.onerror = null;
                el.src = fallback;
              }
            : undefined
        }
        className={cn(
          'hottr-matte-editorial pointer-events-none absolute inset-0 h-full w-full select-none object-cover object-center',
          'transition-transform duration-slow ease-mechanical group-hover:scale-[1.04]',
        )}
      />
      )}

      <span className="hottr-matte-editorial__vignette" aria-hidden />
      <span className="hottr-matte-editorial__grain" aria-hidden />

      {/* Ink scrim — the only gradient canon allows over photography. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-hottr-scrim" />

      {live && <StatusDot status="live" size={7} className="absolute left-2.5 top-2.5" />}

      {typeof index === 'number' && (
        <span className="absolute left-2.5 top-2.5 font-display text-[9px] font-bold uppercase tracking-hottr-marker text-white/48">
          {String(index + 1).padStart(2, '0')}
        </span>
      )}

      {badge && (
        <span className="absolute right-2 top-2 rounded-sm border border-white/[0.18] bg-hottr-ink/70 px-1.5 py-1 font-display text-[9px] font-bold uppercase leading-none tracking-hottr-label text-white backdrop-blur-sm">
          {badge}
        </span>
      )}

      <div className="absolute inset-x-0 bottom-0 space-y-1 p-3">
        <div className="flex items-end justify-between gap-2">
          <h3 className="min-w-0 truncate font-display text-[13px] font-extrabold uppercase tracking-hottr-heading text-white sm:text-[14px]">
            {name}
          </h3>
          {trailing && (
            <span className="shrink-0 font-body text-[12px] font-extrabold text-white sm:text-[13px]">
              {trailing}
            </span>
          )}
        </div>
        {meta && (
          <p className="truncate font-body text-[11px] font-normal text-white/60">{meta}</p>
        )}
        {footnote && (
          <p className="truncate font-body text-[10px] font-normal text-white/45">{footnote}</p>
        )}
      </div>
    </Root>
  );
};
